import React, { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  ConnectionLineType,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import '@xyflow/react/dist/style.css';

const NetworkFlowchart = ({ data }) => {
  console.log('NetworkFlowchart received data:', data);
  
  // Define node dimensions
  const nodeWidth = 120;
  const nodeHeight = 90;

  // Expand/Collapse state (initial: only root(s) visible)
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Auto-expand all nodes on first render so all edges and hierarchy are visible
  useEffect(() => {
    if (expandedIds.size === 0 && Array.isArray(data) && data.length > 0) {
      const collectIds = (nodes, acc) => {
        for (const n of nodes) {
          acc.add(n.id);
          if (n.children && n.children.length) collectIds(n.children, acc);
        }
        return acc;
      };
      const allIds = collectIds(data, new Set());
      setExpandedIds(allIds);
    }
  }, [data, expandedIds.size]);
  
  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Custom node component
  const CustomNode = ({ data }) => {
    const isRootNode = data.level === 0;

    // Chevron indicators
    const showChevron = data.hasChildren;
    const chevron = data.expanded ? '▾' : '▸';

    return (
      <div
        style={{
          padding: '8px 6px',
          border: `2px solid ${isRootNode ? '#22c55e' : '#e5e7eb'}`,
          borderRadius: '8px',
          backgroundColor: isRootNode ? '#f0fdf4' : '#ffffff',
          minWidth: nodeWidth,
          minHeight: nodeHeight,
          textAlign: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          position: 'relative',
        }}
      >
        {/* Handles for edges (top = target, bottom = source) */}
        <Handle type="target" position={Position.Top} id="t" style={{ width: 8, height: 8, background: '#8f5d29' }} />

        {/* Chevron button (toggle expand/collapse) */}
        {showChevron && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); data.onToggle?.(data.id); }}
            title={data.expanded ? 'Collapse' : 'Expand'}
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 18,
              height: 18,
              borderRadius: 3,
              border: '1px solid #e5e7eb',
              background: '#fff',
              cursor: 'pointer',
              lineHeight: '16px',
              fontSize: 12,
              color: '#8f5d29'
            }}
          >
            {chevron}
          </button>
        )}

        {/* Name */}
        <div
          style={{
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#92400e',
            marginTop: showChevron ? 4 : 0,
            marginBottom: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {data.name}
        </div>

        {/* Metrics: X total • Y direct */}
        <div
          style={{
            fontSize: '10px',
            color: '#16a34a',
            fontWeight: 700
          }}
        >
          {data.total} total • {data.direct} direct
        </div>

        <Handle type="source" position={Position.Bottom} id="b" style={{ width: 8, height: 8, background: '#8f5d29' }} />
      </div>
    );
  };

  // Helpers
  const findNodeData = useCallback((nodes, targetId) => {
    for (const node of nodes) {
      if (node.id === targetId) return node;
      if (node.children && node.children.length) {
        const found = findNodeData(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const annotateHierarchy = useCallback((roots) => {
    const map = new Map();
    const dfs = (node, level) => {
      const children = node.children || [];
      let total = 0;
      for (const child of children) {
        const childInfo = dfs(child, level + 1);
        total += childInfo.total + 1;
      }
      const info = { level, direct: children.length, total };
      map.set(node.id, info);
      return info;
    };
    for (const r of roots || []) dfs(r, 0);
    return map;
  }, []);

  const buildVisibleGraph = useCallback((roots, expanded) => {
    const visibleNodeIds = new Set();
    const visibleEdges = [];

    const walk = (node, isRoot) => {
      visibleNodeIds.add(node.id);
      const expandedHere = expanded.has(node.id);
      const children = node.children || [];

      if (expandedHere || isRoot === true) {
        for (const child of children) {
          // Show edge and child only if parent is expanded (or is root and expanded)
          if (expandedHere) {
            visibleEdges.push({ source: node.id, target: child.id });
          }
          // Child is visible if parent is expanded
          if (expandedHere) walk(child, false);
        }
      }
    };

    // Show roots always (but without edges until expanded)
    for (const r of roots || []) {
      walk(r, true);
    }

    return { visibleNodeIds, visibleEdges };
  }, []);

  // Transform data to React Flow format (with expand/collapse)
  const { initialNodes, initialEdges } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return { initialNodes: [], initialEdges: [] };
    }
    
    const annotations = annotateHierarchy(data);
    const { visibleNodeIds, visibleEdges } = buildVisibleGraph(data, expandedIds);

    // Initialize Dagre graph for visible subset
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
      rankdir: 'TB',
      nodesep: 50,
      ranksep: 80,
    });

    // Add visible nodes to dagre
    visibleNodeIds.forEach((id) => {
      dagreGraph.setNode(id, {
        width: nodeWidth,
        height: nodeHeight,
        label: findNodeData(data, id)?.name || id,
      });
    });

    // Add visible edges to dagre
    visibleEdges.forEach(({ source, target }) => {
      dagreGraph.setEdge(source, target);
    });

    // Layout
    dagre.layout(dagreGraph);

    // Build React Flow edges
    const flowEdges = visibleEdges.map(({ source, target }) => ({
      id: `${source}-${target}`,
      source,
      target,
      type: 'smoothstep',
      style: { stroke: '#8f5d29', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#8f5d29' },
    }));

    // Build React Flow nodes
    const flowNodes = [];
    visibleNodeIds.forEach((id) => {
      const dagNode = dagreGraph.node(id);
      const original = findNodeData(data, id);
      const ann = annotations.get(id) || { level: 0, direct: 0, total: 0 };
      if (original && dagNode) {
        flowNodes.push({
          id,
          type: 'custom',
          position: { x: dagNode.x - nodeWidth / 2, y: dagNode.y - nodeHeight / 2 },
          data: {
            id,
            name: original.name,
            total: ann.total,
            direct: ann.direct,
            expanded: expandedIds.has(id),
            hasChildren: ann.direct > 0,
            onToggle: toggleExpand,
            level: ann.level,
          },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
          draggable: true,
        });
      }
    });

    console.log('NetworkFlowchart (visible): nodes', flowNodes.length, 'edges', flowEdges.length);

    return { initialNodes: flowNodes, initialEdges: flowEdges };
  }, [data, expandedIds, annotateHierarchy, buildVisibleGraph, findNodeData, toggleExpand]);

  // React Flow state management
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // React Flow instance
  const rfInstanceRef = useRef(null);
  const [isCompactViewport, setIsCompactViewport] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setIsCompactViewport(false);
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 600px)');
    const updateViewport = event => setIsCompactViewport(event.matches);

    updateViewport(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateViewport);
      return () => mediaQuery.removeEventListener('change', updateViewport);
    }

    mediaQuery.addListener(updateViewport);
    return () => mediaQuery.removeListener(updateViewport);
  }, []);

  // Update nodes and edges when initial data changes
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Handle ReactFlow initialization
  const onInit = useCallback((instance) => {
    rfInstanceRef.current = instance;
    // Immediately fit view on initialization
    const padding = window.innerWidth <= 600 ? 0.1 : 0.2;
    setTimeout(() => {
      instance.fitView({
        padding,
        duration: 400,
        includeHiddenNodes: false
      });
    }, 150);
  }, []);

  // Re-center view when viewport size changes or nodes update
  useEffect(() => {
    if (!rfInstanceRef.current || !nodes || nodes.length === 0) {
      return;
    }

    const instance = rfInstanceRef.current;
    const padding = isCompactViewport ? 0.1 : 0.2;

    const timeoutId = setTimeout(() => {
      instance.fitView({
        padding,
        duration: 300,
        includeHiddenNodes: false
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [nodes, isCompactViewport]);

  const onConnect = useCallback((params) => {
    console.log('Connection attempt:', params);
  }, []);

  // Node types configuration
  const nodeTypes = useMemo(() => ({
    custom: CustomNode,
  }), []);

  return (
    <div className="w-full h-full relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        nodeTypes={nodeTypes}
        connectionLineType={ConnectionLineType.SmoothStep}
        minZoom={0.1}
        maxZoom={2}
        attributionPosition="bottom-left"
      >
        <Background
          variant="dots"
          gap={30}
          size={1}
          color="#e5e7eb"
        />
        <Controls
          showFitView
          showZoom
          showInteractive={false}
          position="bottom-left"
        />
        {!isCompactViewport && (
          <MiniMap
            zoomable
            pannable
            nodeColor="rgba(146, 64, 14, 0.2)"
            nodeStrokeColor="rgba(146, 64, 14, 0.4)"
            maskColor="rgba(255, 255, 255, 0.8)"
          />
        )}
      </ReactFlow>

      {/* Debug info */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 1000,
        }}
      >
        Nodes: {nodes.length} | Edges: {edges.length}
      </div>
    </div>
  );
};

export default NetworkFlowchart;
